import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { activeUrlGalleryLang } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;

    const galleryLang = this.getAttribute('data-url-gallery');
    const activeLang = activeUrlGalleryLang();

    // Hidden locale galleries must not replace themselves. Both listeners would
    // otherwise move the same node, and the visible gallery would keep its
    // previous main image.
    if (galleryLang && galleryLang !== activeLang) return;

    const newMediaGallery = mediaGalleryForLocale(source, galleryLang);

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

/**
 * Section HTML includes both DE and EN galleries. Pick the one for this element,
 * otherwise the gallery for the current URL locale.
 * @param {ParentNode} source
 * @param {string | null} lang
 * @returns {Element | null}
 */
function mediaGalleryForLocale(source, lang) {
  const galleryLang = lang || activeUrlGalleryLang();
  return (
    source.querySelector(`media-gallery[data-url-gallery="${CSS.escape(galleryLang)}"]`) ??
    (source.querySelectorAll('media-gallery').length === 1 ? source.querySelector('media-gallery') : null)
  );
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
